import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import {
  IGeojsonRequest,
  IGeojsonRequestDTO,
} from "app/bundles/requests/shared/models/geojsonRequest";
import { FilterService } from "app/bundles/requests/shared/services/filter.service";
import { UtilService } from "app/bundles/requests/shared/services/util.service";
import { StorageService } from "app/shared/services/storage.service";
import * as _ from "lodash-es";
import { Observable, Subscription, zip } from "rxjs";
import { map, tap } from "rxjs/operators";
import { CoreService } from "../../../../core/core.service";
import { EventApi } from "../api-services/event-api/event-api.models";
import { RequestApi } from "../api-services/request-api/request-api.models";
import { IApiErrorSuccessDTO } from "../models/apiErrorSuccess";
import { Feature } from "../models/feature";
import { IServiceRequestProperties, Properties } from "../models/properties";
import { RequestSourceTypeEnum } from "../models/request";
import { CurrentUserService } from "./current-user.service";
import { ErrorMessageService } from "app/core/error-message.service";

@Injectable()
export class RequestService {
  constructor(
    private storageService: StorageService,
    private requestApiService: RequestApi,
    private utilService: UtilService,
    private coreService: CoreService,
    private eventApiService: EventApi,
    private currentUserService: CurrentUserService,
    private errorMessageService: ErrorMessageService,
  ) {}

  public getGeojson(): Subscription {
    return this.getGeojsonObservable().subscribe((r) =>
      this.coreService.stopSpinner(),
    );
  }

  public getGeojsonObservable(): Observable<IGeojsonRequest> {
    const workorderRequestObservable = this.requestApiService
      .getWorkorderGeojsonData()
      .pipe(
        tap((geojson) => {
          const requestWithBadResolutionDays = geojson.features
            .filter((f) => !f.properties.estimatedCompletionDate)
            .map((f) => f.properties.requestNumber);
          const nbrOfRequestWithBadResolutionDays =
            requestWithBadResolutionDays?.length;

          if (nbrOfRequestWithBadResolutionDays > 0) {
            const firstRequestWithBadResolutionDays =
              requestWithBadResolutionDays[0];

            this.errorMessageService.notifyCustomError(
              new Error(
                `<p>Délai de résolution manquant pour DDD ${firstRequestWithBadResolutionDays} (+${
                  nbrOfRequestWithBadResolutionDays - 1
                } autres).</p><p>L'affichage des demandes hors-délai en sera affecté.</p>`,
              ),
            );
          }
        }),
        map((geojson) => {
          const fromRawFeaturesToFeatures = geojson.features.map((feat) => {
            const feature: Feature = {
              ...feat,
              properties: {
                ...feat.properties,
                estimatedCompletionDate: feat.properties.estimatedCompletionDate
                  ? new Date(feat.properties.estimatedCompletionDate)
                  : undefined,
                creationDatetime: feat.properties.creationDatetime
                  ? new Date(feat.properties.creationDatetime)
                  : undefined,
                source: RequestSourceTypeEnum.workorderRequest,
              },
            };
            return feature;
          });
          const toGeoJSONRequest: IGeojsonRequest = {
            ...geojson,
            features: fromRawFeaturesToFeatures,
          };
          return toGeoJSONRequest;
        }),
      );
    let cptExcluded = [];
    let cptTotal = 0;
    const serviceRequestObservable = this.requestApiService
      .getServiceRequestGeojsonData()
      .pipe(
        map((geojson) => {
          const newFeatures = geojson.features.filter((f) => {
            const isRequestIsForWorkorderRequest = this.isAllowableActivity(
              f.properties.activity.code,
              f.properties.borough.code,
              f.properties.responsible.groupId,
            );
            //
            // Ensure that serviceRequests show on the map can be take in charge by workorder-request.
            //
            // We still can get a serviceRequest for whom current user has access
            // (by means of current user belonging to a group), but this same group is not a
            // workorder-request group.
            //
            // See groupId 155 (ECLAIRAGE ET SIGNALISATION) for such example
            //
            // It's okay to pass borough.code while expecting a boroughId for 2nd parameter here
            // as borough.code represent in fact a borough.id (bad naming)
            //
            if (!isRequestIsForWorkorderRequest) {
              cptExcluded = [...cptExcluded, f.properties.requestNumber];
            }
            cptTotal = cptTotal + 1;
            return isRequestIsForWorkorderRequest;
          });
          return {
            ...geojson,
            features: newFeatures,
          };
        }),
        tap((geojson) => {
          if (cptExcluded?.length > 0) {
            console.log(
              `${cptExcluded.length} of ${cptTotal} serviceRequest excluded because it's for an application other than workorder-request`,
            );
            // console.log(cptExcluded);
          }
          const requestWithBadResolutionDays = geojson.features
            .filter((f) => f.properties.nbrDaysForResolution === undefined)
            .map((f) => f.properties.requestNumber);
          const nbrOfRequestWithBadResolutionDays =
            requestWithBadResolutionDays?.length;

          if (nbrOfRequestWithBadResolutionDays > 0) {
            const firstRequestWithBadResolutionDays =
              requestWithBadResolutionDays[0];

            this.errorMessageService.notifyCustomError(
              new Error(
                `<p>Délai de résolution manquant pour DDS ${firstRequestWithBadResolutionDays} (+${
                  nbrOfRequestWithBadResolutionDays - 1
                } autres).</p><p>L'affichage des demandes hors-délai en sera affecté.</p>`,
              ),
            );
          }
        }),
        map((geojson) => {
          return this.formatGeojsonServiceRequest(geojson);
        }),
      );
    const mergedRequestsObservable = zip(
      workorderRequestObservable,
      serviceRequestObservable,
    ).pipe(
      map((datas: IGeojsonRequest[]) => {
        const workorderRequestsData = datas?.[0];
        const requestsData = datas?.[1];
        this.utilService.updateDefaultProperties(
          workorderRequestsData,
          RequestSourceTypeEnum.workorderRequest,
        );
        this.utilService.updateDefaultProperties(
          requestsData,
          RequestSourceTypeEnum.serviceRequest,
        );
        const mergedData = this.mergeRequests(datas);
        const orderedData = this.orderGeojsonData(mergedData);
        return orderedData;
      }),
      tap((orderedData: IGeojsonRequest) => {
        this.storageService.setGeoJsonRequest(orderedData);
        this.storageService.notifyComponents();

        if (this.storageService.isFirstLoad()) {
          this.storageService.setLastGeoJsonRequest(orderedData);
          this.storageService.setIsFirstLoad(false);
        }
        return;
      }),
    );

    return mergedRequestsObservable;
  }

  private isAllowableActivity(
    activityCode: string,
    boroughId: number,
    groupId: number,
  ): boolean {
    const allowableActivitiesByCode =
      this.currentUserService.getAllowableActivitiesByCode();

    return (
      allowableActivitiesByCode?.[activityCode]?.boroughsById?.[boroughId]
        ?.groupById?.[groupId]?.groupId === groupId
    );
  }

  private formatGeojsonServiceRequest(
    geojson: IGeojsonRequestDTO<IServiceRequestProperties>,
  ): IGeojsonRequest {
    const geojsonFormatted: IGeojsonRequest = {
      ...geojson,
      features: geojson.features.map((feature) => {
        const serviceRequestId = feature.properties.serviceRequestId;
        const properties: Properties = {
          id: serviceRequestId,
          requestNumber: feature.properties.requestNumber,
          status: {
            code: feature.properties.status.code,
            name: feature.properties.status.name,
            statusDatetime: new Date(
              (
                feature.properties as IServiceRequestProperties
              ).status.createdAt,
            ),
          },
          estimatedCompletionDate: this.utilService.getEstimatedCompletionDate(
            new Date(
              (
                feature.properties as IServiceRequestProperties
              ).requestedDatetime,
            ),
            feature.properties.nbrDaysForResolution,
          ),
          creationDatetime: new Date(feature.properties.requestedDatetime),
          formattedAddress: feature.properties.formattedAddress,
          service: {
            code: feature.properties.activity.code,
            name: feature.properties.activity.name,
          },
          description: feature.properties.subject,
          isReactivated: feature.properties.isReactivated,
          isRelaunched: feature.properties.isRelaunched,
          responsible: {
            userName: feature.properties.responsible.userName
              ? feature.properties.responsible.userName
              : undefined,
            userCode: feature.properties.responsible.userCode
              ? feature.properties.responsible.userCode
              : undefined,
            groupName: feature.properties.responsible.groupName,
            groupId: feature.properties.responsible.groupId,
          },
          borough: feature.properties.borough,
          serviceRequestId,
          type: undefined, // Only apply to a workorderRequest
          source: RequestSourceTypeEnum.serviceRequest,
        };
        return { ...feature, properties };
      }),
    };
    return geojsonFormatted;
  }

  private orderGeojsonData(data: IGeojsonRequest): IGeojsonRequest {
    const dataSorted: Feature[] = _.orderBy(
      data.features,
      [
        "properties.source",
        "properties.estimatedCompletionDate",
        "properties.isReactivated",
        "properties.isRelaunched",
      ],
      ["desc", "asc", "desc", "desc"],
    );
    return this.utilService.convertFeatureToGeojson(dataSorted);
  }

  private mergeRequests(data: IGeojsonRequest[]): IGeojsonRequest {
    const workorderRequestFeatures = data[0].features;
    const requestFeatures = data[1].features;
    const requestNumbersHavingWorkorderRequest = workorderRequestFeatures.map(
      (f) => f.properties.requestNumber,
    );
    const requestsNotHavingWorkorderRequest = requestFeatures.filter(
      (r) =>
        !requestNumbersHavingWorkorderRequest.includes(
          r.properties.requestNumber,
        ),
    );
    const mergedRequests: IGeojsonRequest = {
      type: "FeatureCollection",
      features: workorderRequestFeatures.concat(
        requestsNotHavingWorkorderRequest,
      ),
    };
    return mergedRequests;
  }

  public cancelTakeInCharge(ids: number[]): Observable<IApiErrorSuccessDTO> {
    return this.eventApiService.postCancelTakeInCharge(ids);
  }
}
